import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { useNode, useEditingCond } from '@splashthat/splash-cms-engine';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';
import usePrevious from 'utils/hooks/usePrevious';
import { useFlags } from 'utils/react-context';

import ZoomEmbed from '../../../../../../components/elements/Embed/ZoomEmbed';
import ZoomJoinCountdownWrapper from './ZoomJoinCountdownWrapper';
import { Headline, Button, ZoomGatedEmbedContainer, ZoomEmbedMessage } from './ZoomGatedEmbed.styles';

export default function ZoomGatedEmbed({
    theme,
    guest,
    hovered,
    selected,
    embedData,
    descendants,
    isContentEditingDisabled,
}) {
    const node = useNode();
    const [isOpen, setIsOpen] = useState(false);
    const isPreviouslyOpen = usePrevious(isOpen);

    const { zoomSdkObfTokenEnabled } = useFlags();
    const history = useHistory();
    const { search } = useLocation();
    const urlSearchParams = new URLSearchParams(search);
    const authCode = urlSearchParams.get('code');

    const onLeaveMeeting = () => {
        if (zoomSdkObfTokenEnabled) {
            const currentParams = new URLSearchParams(search);
            currentParams.delete('code');
            currentParams.delete('meeting-id');
            const updatedQueryString = currentParams.toString();
            const cleanedUrl = `${window.location.pathname}${updatedQueryString ? `?${updatedQueryString}` : ''}`;
            history.replace(cleanedUrl);
        }
        setIsOpen(false);
    };

    const onEnterMeeting = () => {
        setIsOpen(true);
    };

    const onClickZoomButton = useEditingCond(() => {}, onEnterMeeting);

    const setContent = useCallback(
        (descendantId, text) =>
            node.actions.setProp((editedNode) => {
                editedNode.descendants[descendantId].meta.content = text; // eslint-disable-line no-param-reassign
                return editedNode;
            }),
        [],
    );

    return isOpen || (zoomSdkObfTokenEnabled && authCode) ? (
        <ZoomEmbed
            guest={guest}
            meetingId={embedData.options?.zoom?.meetingId}
            signature={embedData.options?.zoom?.signature}
            password={embedData.options?.zoom?.meetingPassword}
            features={{
                showGuestEmails: embedData.options?.zoom?.showGuestEmailsEnabled,
                supportsChat: embedData.options?.zoom?.chatEnabled,
            }}
            onLeaveMeeting={onLeaveMeeting}
        />
    ) : (
        <ZoomGatedEmbedContainer>
            <Headline
                $fontColor={theme.colors[descendants.zoomHeadline.styles.fontColor]}
                $fontFamily={theme.activeFonts[descendants.zoomHeadline.styles.fontFamily]}
                $fontSize={descendants.zoomHeadline.styles.fontSize}
                hovered={hovered}
                selected={selected}
            >
                <TextEditable
                    setContent={(text) => setContent('zoomHeadline', text)}
                    disabled={isContentEditingDisabled}
                >
                    {descendants.zoomHeadline.meta.content}
                </TextEditable>
            </Headline>
            {zoomSdkObfTokenEnabled ? (
                <ZoomEmbedMessage
                    $fontColor={theme.colors[descendants.zoomEmbedMessage.styles.fontColor]}
                    $fontFamily={theme.activeFonts[descendants.zoomEmbedMessage.styles.fontFamily]}
                    $fontSize={descendants.zoomEmbedMessage.styles.fontSize}
                    hovered={hovered}
                    selected={selected}
                >
                    <TextEditable
                        setContent={(text) => setContent('zoomEmbedMessage', text)}
                        disabled={isContentEditingDisabled}
                    >
                        {descendants.zoomEmbedMessage.meta.content}
                    </TextEditable>
                </ZoomEmbedMessage>
            ) : null}
            <ZoomJoinCountdownWrapper
                isOpen={isOpen}
                isPreviouslyOpen={isPreviouslyOpen}
                joinButton={
                    <Button
                        $fontFamily={theme.activeFonts[descendants.zoomButton.styles.fontFamily]}
                        $fontColor={theme.colors[descendants.zoomButton.styles.fontColor]}
                        $fontColorHover={theme.colors[descendants.zoomButton.styles.fontColorHover]}
                        $backgroundColor={theme.colors[descendants.zoomButton.styles.backgroundColor]}
                        $backgroundColorHover={theme.colors[descendants.zoomButton.styles.backgroundColorHover]}
                        $borderColor={theme.colors[descendants.zoomButton.styles.borderColor]}
                        $borderColorHover={theme.colors[descendants.zoomButton.styles.borderColorHover]}
                        $borderWidth={descendants.zoomButton.styles.borderWidth}
                        $borderRadius={descendants.zoomButton.styles.borderRadius}
                        $fontSize={descendants.zoomButton.styles.fontSize}
                        hovered={hovered}
                        selected={selected}
                        onClick={onClickZoomButton}
                    >
                        <TextEditable
                            setContent={(text) => setContent('zoomButton', text)}
                            disabled={isContentEditingDisabled}
                        >
                            {descendants.zoomButton.meta.content}
                        </TextEditable>
                    </Button>
                }
            />
        </ZoomGatedEmbedContainer>
    );
}

ZoomGatedEmbed.propTypes = {
    theme: PropTypes.object,
    guest: PropTypes.object,
    embedData: PropTypes.object,
    descendants: PropTypes.object,
    isContentEditingDisabled: PropTypes.bool,
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
};

ZoomGatedEmbed.defaultProps = {
    theme: {},
    guest: {},
    embedData: {},
    descendants: {},
    isContentEditingDisabled: false,
};
